// RefreshSyncModal.tsx — the "Refresh EKD & BC" review popup (grouped, compact).
// Two groups, both collapsed by default:
//   • Governed fields   — have a governance status; promote per the chosen statuses.
//   • Observed lists    — values taken from the data (no client list). PHASE I: they promote ONLY
//                         when the SME ticks "Accept"; a rule on them finds no current defects and
//                         only flags NEW codes. Every field shows the share of rows a rule would pass.
// Only a GOVERNED field that would promote 0 values is a real "empty" (its BC controls drop).
import React, { useEffect, useState } from "react";
import {
  lookup_promotionPreview, lookup_syncWithRules,
  type PromotionPreview,
} from "../api/lkpAPI";
import { useAuth } from "../hooks/useAuth";

const OPTIONAL_STATUSES = ["Draft", "Candidate", "Submitted For Approval", "Not Documented Yet", "Obsolete"];
const TONE: Record<string, string> = {
  Approved: "#2f7a4f", Obsolete: "#b23a30", Draft: "#b8860b",
  Candidate: "#43607e", "Submitted For Approval": "#6a5acd", "Not Documented Yet": "#8a97a3",
};

export default function RefreshSyncModal({ entityId, onClose, onDone }:
  { entityId: string; onClose: () => void; onDone?: (r: any) => void }) {
  const { user } = useAuth();
  const [globalStatuses, setGlobalStatuses] = useState<string[]>([]);
  const [overrides, setOverrides] = useState<Record<string, string[]>>({});
  const [acceptObs, setAcceptObs] = useState<string[]>([]);   // PHASE I: observed lists the SME accepts
  const [preview, setPreview] = useState<PromotionPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [openGov, setOpenGov] = useState(false);
  const [openProf, setOpenProf] = useState(false);
  const [expand, setExpand] = useState<string | null>(null);

  const loadPreview = async () => {
    setLoading(true); setErr(null);
    try { setPreview(await lookup_promotionPreview(entityId, globalStatuses, overrides, acceptObs)); }
    catch (e: any) { setErr(e?.detail || e?.message || "Couldn't compute the preview"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadPreview(); /* eslint-disable-next-line */ }, [entityId, globalStatuses, overrides, acceptObs]);
  const toggleAccept = (eid: string) =>
    setAcceptObs((cur) => cur.includes(eid) ? cur.filter((x) => x !== eid) : [...cur, eid]);
  const covText = (c?: { fields: { valid_pct: number | null }[] } | null) =>
    c && c.fields && c.fields.length && c.fields[0].valid_pct != null ? `${c.fields[0].valid_pct}%` : "—";

  const toggleGlobal = (s: string) =>
    setGlobalStatuses((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
  const setFieldOverride = (eid: string, statuses: string[] | null) =>
    setOverrides((cur) => { const n = { ...cur }; if (statuses === null) delete n[eid]; else n[eid] = statuses; return n; });

  const sync = async () => {
    setSyncing(true); setErr(null);
    try {
      const r = await lookup_syncWithRules(entityId, globalStatuses, overrides, user?.full_name || user?.email || "SME", acceptObs);
      onDone?.(r); onClose();
    } catch (e: any) { setErr(e?.detail || e?.message || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const empties = preview?.empty_count || 0;
  const govCount = preview?.governed_count || 0;
  const noStatusCount = preview?.unstatused_count || 0;

  return (
    <div className="rsm-bg" onClick={onClose}>
      <style>{CSS}</style>
      <div className="rsm" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <div className="rsm-h">
          <div className="rsm-title">Refresh EKD &amp; BC — choose what promotes</div>
          <button className="rsm-x" onClick={onClose}>×</button>
        </div>

        <div className="rsm-body">
          <div className="rsm-global">
            <div className="rsm-lbl">For fields that have a governance status, promote:</div>
            <div className="rsm-chips">
              <span className="rsm-chip locked" title="Approved always promotes">✓ Approved</span>
              {OPTIONAL_STATUSES.map((s) => (
                <button key={s} className={"rsm-chip" + (globalStatuses.includes(s) ? " on" : "")}
                  style={globalStatuses.includes(s) ? { background: TONE[s], borderColor: TONE[s], color: "#fff" } : {}}
                  onClick={() => toggleGlobal(s)}>{globalStatuses.includes(s) ? "✓ " : ""}{s}</button>
              ))}
            </div>
          </div>

          {loading ? <div className="rsm-muted">Computing what would promote…</div>
            : err ? <div className="rsm-err">{err}</div>
            : !preview ? null : (
              <>
                <div className="rsm-summary">
                  <div className="rsm-sumrow"><b>{preview.total_promote}</b> values will promote to EKD across <b>{preview.total_fields}</b> fields</div>
                  <div className="rsm-sumbreak">
                    <span>• <b>{govCount}</b> field{govCount === 1 ? "" : "s"} with a governance status</span>
                    <span>• <b>{noStatusCount}</b> field{noStatusCount === 1 ? "" : "s"} with no status yet → promote as <span style={{ color: TONE.Approved, fontWeight: 600 }}>Approved</span></span>
                    {empties > 0
                      ? <span className="rsm-warn">⚠ <b>{empties}</b> governed field{empties === 1 ? "" : "s"} would promote nothing — their BC controls will drop</span>
                      : <span className="rsm-ok">✓ no fields will lose controls</span>}
                  </div>
                </div>

                {govCount > 0 && (
                  <div className="rsm-group">
                    <div className="rsm-legend">
                      <span className="rsm-legendlbl">Status colours:</span>
                      {["Approved", ...OPTIONAL_STATUSES].map((s) => (
                        <span key={s} className="rsm-legenditem">
                          <i style={{ background: TONE[s] }} />{s}
                        </span>
                      ))}
                    </div>
                    <div className="rsm-grouphead" onClick={() => setOpenGov((v) => !v)}>
                      <span className="rsm-caret">{openGov ? "▾" : "▸"}</span>
                      <b>Fields with a governance status</b><span className="rsm-gcount">{govCount}</span>
                      {empties > 0 && <span className="rsm-gwarn">{empties} would lose controls</span>}
                    </div>
                    {openGov && (
                      <div className="rsm-list">
                        {preview.governed_fields.map((f) => {
                          const isEmpty = f.would_promote === 0;
                          const ov = overrides[f.entity_id];
                          const pct = f.canonical_count ? Math.round((f.would_promote / f.canonical_count) * 100) : 0;
                          const statuses = Object.entries(f.by_status || {}).filter(([k]) => k && k !== "(none)");
                          return (
                            <div key={f.entity_id} className={"rsm-field" + (isEmpty ? " empty" : "") + (f.overridden ? " ov" : "")}>
                              <div className="rsm-frow" onClick={() => setExpand(expand === f.entity_id ? null : f.entity_id)}>
                                <span className="rsm-caret">{expand === f.entity_id ? "▾" : "▸"}</span>
                                <div className="rsm-fmain">
                                  <div className="rsm-fnamerow">
                                    <b className={isEmpty ? "rsm-fnameempty" : ""}>{f.entity_name || f.entity_id}</b>
                                    {f.overridden && <span className="rsm-ovtag">custom</span>}
                                    {isEmpty && <span className="rsm-emptytag">⚠ needs attention — nothing promotes</span>}
                                    <span className="rsm-fcount">{f.would_promote}<span className="rsm-fcslash">/{f.canonical_count}</span></span>
                                    <span className="rsm-cov" title="Share of populated rows a lookup rule would pass: today → with your choice">
                                      rows pass {covText(f.data_coverage_now)} → <b>{covText(f.data_coverage_after)}</b></span>
                                  </div>
                                  <div className="rsm-bar" title={`${pct}% of values will promote`}>
                                    <div className="rsm-barfill" style={{ width: pct + "%", background: isEmpty ? "#e0c27a" : "#2f7a4f" }} />
                                  </div>
                                </div>
                                <span className="rsm-dots">{statuses.map(([k, v]) => (
                                  <span key={k} className={"rsm-dot" + (k === "Approved" ? " appr" : "")}
                                    title={`${v} ${k}`} style={{ ["--dot" as any]: TONE[k] || "#8a97a3" }}>
                                    <i /> {v}</span>
                                ))}</span>
                              </div>
                              {expand === f.entity_id && (
                                <div className="rsm-override">
                                  <span className="rsm-ovlbl">Override:</span>
                                  <span className="rsm-chip locked sm">✓ Approved</span>
                                  {OPTIONAL_STATUSES.map((s) => {
                                    const base = ov || globalStatuses; const on = base.includes(s);
                                    return (
                                      <button key={s} className={"rsm-chip sm" + (on ? " on" : "")}
                                        style={on ? { background: TONE[s], borderColor: TONE[s], color: "#fff" } : {}}
                                        onClick={() => { const b = ov || globalStatuses; setFieldOverride(f.entity_id, b.includes(s) ? b.filter((x) => x !== s) : [...b, s]); }}>
                                        {on ? "✓ " : ""}{s}</button>
                                    );
                                  })}
                                  {f.overridden && <button className="rsm-clearov" onClick={() => setFieldOverride(f.entity_id, null)}>reset</button>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {noStatusCount > 0 && (
                  <div className="rsm-group">
                    <div className="rsm-grouphead" onClick={() => setOpenProf((v) => !v)}>
                      <span className="rsm-caret">{openProf ? "▾" : "▸"}</span>
                      <b>Observed lists (taken from the data)</b><span className="rsm-gcount">{noStatusCount}</span>
                      <span className="rsm-gnote">promote only if you accept them — a rule on such a list finds no current defects, it only flags new codes</span>
                    </div>
                    {openProf && (
                      <div className="rsm-list">
                        {preview.unstatused_fields.map((f) => (
                          <div key={f.entity_id} className="rsm-field">
                            <div className="rsm-frow">
                              <span className="rsm-caret" />
                              <b>{f.entity_name || f.entity_id}</b>
                              <span className="rsm-fcount">{f.canonical_count} values</span>
                              <span className="rsm-cov" title="Share of populated rows covered by this list (always 100% for a list taken from the data)">rows {covText(f.data_coverage)}</span>
                              <label className="rsm-acc" title={f.meaning || ""}>
                                <input type="checkbox" checked={acceptObs.includes(f.entity_id)} onChange={() => toggleAccept(f.entity_id)} />
                                {" "}Accept as list</label>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
        </div>

        <div className="rsm-foot">
          {empties > 0 && <span className="rsm-footwarn">⚠ {empties} field{empties === 1 ? "" : "s"} will lose BC controls</span>}
          <span className="rsm-sp" />
          <button className="rsm-btn" onClick={onClose} disabled={syncing}>Cancel</button>
          <button className="rsm-btn go" onClick={sync} disabled={syncing || loading}>
            {syncing ? "Syncing…" : "Sync EKD & BC"}</button>
        </div>
      </div>
    </div>
  );
}

const CSS = `
.rsm-bg{position:fixed;inset:0;background:rgba(14,36,54,.55);display:grid;place-items:center;z-index:95;padding:20px}
.rsm{background:#fff;border-radius:14px;width:min(760px,96vw);max-height:86vh;display:flex;flex-direction:column;box-shadow:0 24px 70px rgba(0,0,0,.35);overflow:hidden}
.rsm-h{display:flex;align-items:center;padding:15px 20px;background:#0e2436;color:#fff}
.rsm-title{font-size:16px;font-weight:600}.rsm-x{all:unset;cursor:pointer;margin-left:auto;font-size:20px;color:#9fb4c8}
.rsm-body{padding:16px 20px;overflow-y:auto;flex:1}
.rsm-global{background:#f6f9fb;border:1px solid #e3ebf0;border-radius:10px;padding:12px 14px;margin-bottom:14px}
.rsm-lbl{font-size:13px;font-weight:600;color:#33404e;margin-bottom:8px}
.rsm-chips{display:flex;flex-wrap:wrap;gap:6px}
.rsm-chip{all:unset;cursor:pointer;font-size:12px;border:1px solid #cbd5e0;border-radius:20px;padding:3px 12px;color:#5f6e7d}
.rsm-chip.sm{font-size:11px;padding:2px 9px}.rsm-chip.on{font-weight:600}
.rsm-chip.locked{background:#2f7a4f;border-color:#2f7a4f;color:#fff;cursor:default;font-weight:600}
.rsm-muted{color:#8a97a3;font-size:13px;padding:16px 0}
.rsm-err{color:#b23a30;font-size:13px;padding:12px;background:#fdf1f0;border:1px solid #f3d0cc;border-radius:8px}
.rsm-summary{background:#fbfdfe;border:1px solid #eef2f6;border-radius:10px;padding:12px 14px;margin-bottom:14px}
.rsm-sumrow{font-size:14px;color:#33404e;margin-bottom:6px}
.rsm-sumbreak{display:flex;flex-direction:column;gap:3px;font-size:12.5px;color:#5f6e7d}
.rsm-sumbreak .rsm-warn{color:#8a6d3b}.rsm-sumbreak .rsm-ok{color:#2f7a4f}
.rsm-group{border:1px solid #eef2f6;border-radius:10px;margin-bottom:8px;overflow:hidden}
.rsm-legend{display:flex;flex-wrap:wrap;align-items:center;gap:12px;padding:8px 14px;background:#fbfdfe;border-bottom:1px solid #eef2f6}
.rsm-legendlbl{font-size:11px;color:#8a97a3;font-weight:600}
.rsm-legenditem{display:inline-flex;align-items:center;gap:5px;font-size:11px;color:#5f6e7d}
.rsm-legenditem i{width:8px;height:8px;border-radius:50%;display:inline-block}
.rsm-grouphead{display:flex;align-items:center;gap:8px;padding:11px 14px;cursor:pointer;background:#f8fafb;font-size:13.5px}
.rsm-grouphead:hover{background:#f2f6f8}
.rsm-gcount{font-family:var(--font-mono,monospace);font-size:11.5px;background:#eef1f5;color:#5f6e7d;border-radius:20px;padding:1px 8px}
.rsm-gwarn{font-size:11.5px;color:#8a6d3b;margin-left:auto}
.rsm-gnote{font-size:11.5px;color:#8a97a3;margin-left:auto}
.rsm-list{max-height:300px;overflow-y:auto;padding:4px 8px 8px}
.rsm-field{border-bottom:1px solid #f5f7f9}
.rsm-field.empty{background:#fdf8ec;box-shadow:inset 3px 0 0 #d9a441}
.rsm-frow{display:flex;align-items:center;gap:8px;padding:8px 6px;cursor:pointer;font-size:12.5px}
.rsm-frow:hover{background:#f8fafb}
.rsm-caret{color:#8a97a3;font-size:11px;width:10px;display:inline-block}
.rsm-fmain{flex:1;min-width:0}
.rsm-fnamerow{display:flex;align-items:center;gap:8px}
.rsm-fnameempty{color:#8a6d3b}
.rsm-fcount{margin-left:auto;font-family:var(--font-mono,monospace);font-size:12px;font-weight:600;color:#2f7a4f}
.rsm-fcslash{color:#a8b2bc;font-weight:400}
.rsm-bar{height:5px;background:#eef1f5;border-radius:3px;margin-top:5px;overflow:hidden}
.rsm-barfill{height:100%;border-radius:3px;transition:width .15s}
.rsm-dots{display:flex;gap:9px;margin-left:12px;flex-shrink:0}
.rsm-dot{display:inline-flex;align-items:center;gap:3px;font-size:11px;color:#8a97a3;font-family:var(--font-mono,monospace)}
.rsm-dot i{width:7px;height:7px;border-radius:50%;background:var(--dot);display:inline-block}
.rsm-dot.appr{color:#2f7a4f;font-weight:600}
.rsm-ovtag{font-size:10px;background:#e0f3f1;color:#0b5f5e;border-radius:20px;padding:1px 7px}
.rsm-emptytag{font-size:11px;color:#b8860b}
.rsm-fstatuses{margin-left:auto;display:flex;gap:8px}
.rsm-mini{font-size:10.5px}
.rsm-cov{font-size:11px;color:#5f6e7d;margin-left:10px;white-space:nowrap}
.rsm-acc{font-size:11.5px;color:#0e7c7b;margin-left:auto;cursor:pointer;white-space:nowrap}
.rsm-override{display:flex;flex-wrap:wrap;align-items:center;gap:6px;padding:6px 10px 10px 28px;background:#fbfdfe}
.rsm-ovlbl{font-size:11.5px;color:#5f6e7d;margin-right:4px}
.rsm-clearov{all:unset;cursor:pointer;font-size:11px;color:#0e7c7b;text-decoration:underline;margin-left:6px}
.rsm-foot{display:flex;align-items:center;gap:12px;padding:14px 20px;border-top:1px solid #eef2f6}
.rsm-footwarn{font-size:12px;color:#8a6d3b}.rsm-sp{flex:1}
.rsm-btn{all:unset;cursor:pointer;font-size:13px;padding:8px 16px;border:1px solid #dce3ea;border-radius:8px;color:#5f6e7d}
.rsm-btn.go{background:#0e7c7b;color:#fff;border-color:#0e7c7b;font-weight:600}
.rsm-btn:disabled{opacity:.5;cursor:default}
`;
